/** 不同请求服务的环境类型 */
export type ServiceEnvType = "dev" | "test" | "prod"

/** 远程 API 域名（可通过 VITE_API_HOST 覆盖） */
export const API_HOST = import.meta.env.VITE_API_HOST?.replace(/\/$/, "") || "https://api.lamp.yunludeng.com"

/** 不同请求服务的环境配置 */
export const serviceConfig: Record<ServiceEnvType, Record<string, string>> = {
  dev: {
    url: `${API_HOST}/`,
    urlLocal: "http://127.0.0.1:8787",
    /** 参考项目（bendi）本地网关，登录 /api/api/login */
    urlRef: "http://localhost:9980",
    ws: "wss://wss.yunludeng.com",
    wws: "wss://wss.yunludeng.com",
    https_url: `${API_HOST}/`,
    wsTarget: import.meta.env.VITE_WS_TARGET || "ws://127.0.0.1:23400"
  },
  test: {},
  prod: {
    url: `${API_HOST}/`,
    ws: "wss://wss.yunludeng.com",
    wws: "wss://wss.yunludeng.com",
    https_url: `${API_HOST}/`,
    wsTarget: "wss://wss.yunludeng.com"
  }
}

export function getServiceEnv(): ServiceEnvType {
  if (import.meta.env.DEV) return "dev"
  if (import.meta.env.MODE === "test") return "test"
  return "prod"
}

/**
 * 参考项目模式（与上一版 bendi / LED-B 款一致）
 * - API：vite 代理 → localhost:9980（路径 /api/xxx → /api/api/xxx）
 * - 登录：JSON
 * - WS：wss://wss.yunludeng.com（token 与参考项目同源，可收 yb_screenshot）
 */
export function useRefDevApi(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_API_REF === "true"
}

/** kaifa HTTP + 云端 wss */
export function useCloudDevApi(): boolean {
  return import.meta.env.DEV
    && import.meta.env.VITE_API_CLOUD === "true"
    && !useRefDevApi()
}

/** 内网盒子 192.168.100.12 */
export function isLocalBackend(): boolean {
  if (import.meta.env.DEV) {
    return !useCloudDevApi() && !useRefDevApi()
  }
  return isLocalHttpApi(getHttpBaseUrl())
}

/** 参考项目 / kaifa 用 JSON 登录；192.168 用 FormData */
export function useJsonLogin(): boolean {
  return useRefDevApi() || useCloudDevApi()
}

function normalizeBaseUrl(url: string): string {
  return url.endsWith("/") ? url : `${url}/`
}

function getLocalApiPort(): string {
  return import.meta.env.VITE_API_PORT?.trim() || "8787"
}

function getLocalWsPort(): string {
  return import.meta.env.VITE_WS_PORT?.trim() || "23400"
}

function isAutoHost(value?: string): boolean {
  const v = value?.trim().toLowerCase()
  return !v || v === "auto"
}

/** 浏览器侧：跟随当前访问地址的 hostname（比写死 127.0.0.1 更灵活） */
function resolveRuntimeLocalHttpBase(envBase: string | undefined): string {
  if (!isAutoHost(envBase)) return normalizeBaseUrl(envBase!)
  if (typeof location !== "undefined") {
    return `http://${location.hostname}:${getLocalApiPort()}/`
  }
  return `http://127.0.0.1:${getLocalApiPort()}/`
}

function resolveRuntimeLocalWs(envWs: string | undefined): string {
  if (!isAutoHost(envWs)) return envWs!.trim()
  if (typeof location !== "undefined") {
    const protocol = location.protocol === "https:" ? "wss" : "ws"
    return `${protocol}://${location.hostname}:${getLocalWsPort()}`
  }
  return `ws://127.0.0.1:${getLocalWsPort()}`
}

export function getHttpBaseUrl(): string {
  const config = serviceConfig[getServiceEnv()]
  if (import.meta.env.DEV) {
    // 开发环境统一走 vite 代理，避免直连后端跨域导致验证码不显示
    if (typeof location !== "undefined") {
      return `${location.protocol}//${location.host}/`
    }
    if (useRefDevApi()) return config.urlRef || "http://localhost:9980/"
    if (useCloudDevApi()) return config.url || `${API_HOST}/`
    return resolveRuntimeLocalHttpBase("auto")
  }

  const envBase = import.meta.env.VITE_BASE_URL
  if (isAutoHost(envBase)) {
    return resolveRuntimeLocalHttpBase(envBase)
  }
  if (envBase?.trim()) return normalizeBaseUrl(envBase)

  // 未配置 VITE_BASE_URL 时走当前页面同源（需 nginx 等反代 /api）
  if (typeof location !== "undefined") {
    return `${location.protocol}//${location.host}/`
  }

  return config.url || ""
}

/** LED 媒体文件预览地址：/file/led?id=xxx（开发环境走 vite /file 代理） */
export function getLedFileUrl(id: number | string): string {
  const base = getHttpBaseUrl()
  return `${base.endsWith("/") ? base : `${base}/`}file/led?id=${id}`
}

export function getRefApiTarget(): string {
  return serviceConfig.dev.urlRef || "http://localhost:9980"
}

export function isLocalHttpApi(url: string = getHttpBaseUrl()): boolean {
  try {
    const host = new URL(url).hostname
    return host === "localhost"
      || host === "127.0.0.1"
      || /^192\.168\./.test(host)
      || /^10\./.test(host)
  } catch {
    return false
  }
}

export function getWsProxyTarget(): string {
  const config = serviceConfig[getServiceEnv()]
  return config.wsTarget || config.ws || config.wws
}

export function getCloudWsUrl(): string {
  const config = serviceConfig[getServiceEnv()]
  return config.wws || config.ws
}

export function getLocalWsUrl(): string {
  if (import.meta.env.VITE_WS_DIRECT === "true") {
    return getWsProxyTarget()
  }
  const isHttps = typeof location !== "undefined" && location.protocol === "https:"
  const protocol = isHttps ? "wss" : "ws"
  return `${protocol}://${location.host}/api/ws`
}

/** 参考 / 云端模式：仅 wss；192.168 模式：可降级本地 WS */
export function getWsUrl(preferLocal = false): string {
  const config = serviceConfig[getServiceEnv()]
  const isHttps = typeof location !== "undefined" && location.protocol === "https:"

  if (!import.meta.env.DEV) {
    const envWs = import.meta.env.VITE_WS_BASE_URL
    if (isAutoHost(envWs)) return resolveRuntimeLocalWs(envWs)
    if (envWs?.trim()) return envWs.trim()
    return isHttps ? (config.wws || config.ws) : config.ws
  }

  if (useRefDevApi() || useCloudDevApi()) {
    return getCloudWsUrl()
  }

  if (preferLocal || import.meta.env.VITE_WS_LOCAL === "true") {
    return getLocalWsUrl()
  }

  return getCloudWsUrl()
}

export function isCloudWsUrl(url: string): boolean {
  return url.includes("yunludeng.com")
}

/** 参考 / 云端模式不应降级到本地 WS */
export function allowWsLocalFallback(): boolean {
  return import.meta.env.DEV && isLocalBackend()
}
